import React, { FC } from 'react';
import { Link } from '@amzn/storm-ui';
import styled from 'styled-components';
import useIntl from '@i18n/useIntl';
import {
  AMAZON_ADS_AGREEMENT_PATH,
  COMBINE_ACCOUNTS_REQUEST_ACCESS_AGREEMENT_LINK_KEY,
} from '@features/combine-accounts/constants';

/*
  An anchor rather than a TextButton, for line breaking. A button is laid out as an
  atomic inline-level box whatever its `display`, and the line breaker may break after
  such a box even with no whitespace there, which put the acknowledgement's closing
  full stop on a line of its own whenever the link landed near the right edge. An
  anchor is ordinary inline text, so there is no break opportunity between the link's
  last glyph and the punctuation that follows it.

  Typography is inherited rather than stated: every caller embeds this mid-sentence,
  and the approved designs give the link the same family, weight, size, line height
  and letter spacing as the sentence around it.
*/
const InlineLink = styled.span`
  && {
    font: inherit;
    letter-spacing: inherit;
  }

  && a {
    font: inherit;
    letter-spacing: inherit;
  }
`;

/**
 * Click-through to the Amazon Ads Agreement, for use as the `{agreement}` argument of an
 * acknowledgement string.
 *
 * Shared because both combine confirmation modals render the same acknowledgement from the same
 * string id, so a link built separately in each would let them drift, and a caller that forgot to
 * supply it would render the raw `{agreement}` placeholder to the user.
 */
const AmazonAdsAgreementLink: FC = () => {
  const { intl } = useIntl();

  return (
    <InlineLink
      /*
        Capture rather than bubble, and on a wrapper rather than on the anchor. Capturing on
        the way down suppresses the label before the anchor's own handling, and putting it on
        the wrapper means it does not depend on Link forwarding a handler: the storm-ui test
        double forwards only href and children, so a handler on the Link itself is untestable
        here even though the real component would pass it through. A span and an anchor are
        both inline, so nothing here introduces an atomic box.
      */
      onClickCapture={(event: React.MouseEvent) => {
        /*
          Storm wraps a Checkbox label in a <label>, so a bare click here would both open
          the agreement and toggle the acknowledgement the label is bound to. Suppressing
          the click's default action keeps the two independent, which matters because the
          checkbox records acceptance of that agreement.

          A modified click is left alone: the browser opens it in a new tab or window
          without activating the label, so suppressing it would only take that choice
          away. Only the modifier keys are checked, because a middle click arrives as
          `auxclick` rather than `click` and never reaches this handler.
        */
        if (
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.button !== 0
        ) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        window.open(AMAZON_ADS_AGREEMENT_PATH, '_blank', 'noopener,noreferrer');
      }}
    >
      <Link href={AMAZON_ADS_AGREEMENT_PATH}>
        {intl.getMessage(COMBINE_ACCOUNTS_REQUEST_ACCESS_AGREEMENT_LINK_KEY)}
      </Link>
    </InlineLink>
  );
};

export default AmazonAdsAgreementLink;
